import { isAdmin } from '@platewell/schema';
import { ACTIVE_HOUSEHOLD_KEY, HOUSEHOLD_LIST_KEY } from '@platewell/sync-client';
import type {
  AccountDeletionPreview,
  AccountSecurityResponse,
  DeleteAccountRequest,
  AdminHouseholdDetail,
  AdminHouseholdRow,
  BillingSummary,
  CheckoutResponse,
  CreateInvitedHouseholdRequest,
  CreateInvitedHouseholdResponse,
  AdminListResponse,
  AdminOverview,
  AdminUserRow,
  HouseholdSummary,
  ImportFileDto,
  ImportImageResponse,
  ImportSessionDto,
  ImportSessionSummaryDto,
  ImpersonationState,
  ShareManifest,
} from '@platewell/schema';

/** Same-origin in dev thanks to the Vite proxy; deployed Worker serves SPA + API together. */
export const API_BASE_URL = '';

/**
 * The schema's `SessionResponse`, with `permissions` relaxed back to optional.
 *
 * The server always sends it; a session restored from localStorage was cached by an older
 * build that did not, which is exactly the "we do not know yet" case lib/session.tsx has
 * to keep telling apart from "not an admin".
 */
export interface Session {
  user: {
    id: string;
    name: string;
    email: string;
    image?: string | null;
    permissions?: string[];
  };
  /** Present only while an admin is acting as this user. */
  impersonation?: ImpersonationState;
}

/** Whether to offer the admin section. Presentation only; the API re-checks every request. */
export function sessionIsAdmin(session: Session | null | undefined): boolean {
  return isAdmin(session?.user);
}

/**
 * A refusal from the API with its machine-readable code kept intact.
 *
 * The server answers failures with `{ error: '<code>' }`, and throwing `new Error(code)`
 * is what put strings like `quota_exceeded` in front of the user. Callers that need to
 * react to a particular refusal branch on `code`; everyone else renders `message`, which
 * is written for a human to read.
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    /** Recipe slots still free on the plan. Sent only with `quota_exceeded`. */
    readonly remaining: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Codes are `snake_case`; anything else the server put in `error` is already a sentence.
 *
 * Import sessions are the reason for the distinction: a URL that could not be fetched
 * comes back as its own prose ("URL fetch failed"), in the same field as the codes.
 */
const ERROR_CODE_PATTERN = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

const ERROR_CODE_MESSAGES: Record<string, string> = {
  quota_exceeded: 'Your free plan is full.',
  recipe_ceiling: 'This household has reached the 2,000-recipe maximum.',
  read_only: 'You have read-only access to this household.',
  subscription_lapsed: 'This household\u2019s subscription payment did not go through.',
  forbidden: 'You do not have access to this household.',
  not_found: 'That is no longer here.',
  invalid_request: 'The request could not be understood.',
  no_files: 'No supported recipe files were found.',
  too_many_files: 'That is more files than a single import can take.',
  file_too_large: 'That file is too large to import.',
  cannot_revoke_current: 'That is this device. Use Sign out to end this session.',
  forbidden_while_impersonating: 'Not available while acting as another user.',
  // Deleting an account.
  invalid_password: 'That password is not correct.',
  confirmation_mismatch: 'That is not the email address on this account.',
  // Only invite links answer with these. The last two also refuse sending an invite.
  expired: 'This invite has expired. Ask for a new one.',
  already_used: 'This invite has already been used.',
  household_deleted: 'The household this invite was for has been deleted.',
  household_full: 'This household has reached its limit of 10 people, including pending invites.',
  // Sending an invite from, or accepting one into, a household on the free plan.
  subscription_required:
    'The free plan is for one person. This household needs a subscription before anyone else can join.',
};

async function apiError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as {
    error?: string;
    remaining?: number;
  } | null;
  const raw = body?.error;
  if (raw == null || raw === '') {
    return new ApiError('unknown', `request failed (${response.status})`, response.status);
  }
  const isCode = ERROR_CODE_PATTERN.test(raw);
  return new ApiError(
    isCode ? raw : 'message',
    isCode ? (ERROR_CODE_MESSAGES[raw] ?? raw) : raw,
    response.status,
    typeof body?.remaining === 'number' ? body.remaining : null,
  );
}

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: init?.body == null ? undefined : { 'content-type': 'application/json' },
    ...init,
  });
  if (!response.ok) throw await apiError(response);
  return (await response.json()) as T;
}

const SESSION_KEY = 'platewell.session';

/** The last session the server confirmed, so a cold start with no network still opens. */
export function cachedSession(): Session | null {
  const raw = window.localStorage.getItem(SESSION_KEY);
  if (raw == null) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

/**
 * Being offline is not being signed out.
 *
 * A failed request tells us nothing about whether the session is still valid, so the cached
 * one stands until the server actually says otherwise. Only a 401 clears it; otherwise
 * reopening the app on a plane would drop someone at the sign-in screen with no way back in.
 */
export async function fetchSession(): Promise<Session | null> {
  let response: Response;
  try {
    // Not Better Auth's own `get-session`: that one can only answer with the account that
    // authenticated, and while an admin is impersonating the app has to be driven by the
    // customer instead. `/api/session` resolves that and says who is behind it.
    response = await fetch(`${API_BASE_URL}/api/session`, { credentials: 'include' });
  } catch {
    return cachedSession();
  }

  if (response.status === 401 || response.status === 403) {
    window.localStorage.removeItem(SESSION_KEY);
    return null;
  }
  if (!response.ok) return cachedSession();

  const session = (await response.json().catch(() => null)) as Session | null;
  if (session?.user == null) {
    window.localStorage.removeItem(SESSION_KEY);
    return null;
  }
  window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

function authErrorMessage(body: unknown, status: number): string {
  if (body != null && typeof body === 'object') {
    const record = body as { error?: string; message?: string };
    if (typeof record.message === 'string' && record.message.length > 0) return record.message;
    if (typeof record.error === 'string' && record.error.length > 0) return record.error;
  }
  return `request failed (${status})`;
}

async function authJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(authErrorMessage(payload, response.status));
  return payload as T;
}

export async function signIn(email: string, password: string): Promise<Session> {
  await authJson('/api/auth/sign-in/email', { email, password });
  const session = await fetchSession();
  if (session == null) throw new Error('Signed in, but no session was returned');
  return session;
}

/**
 * `inviteToken` makes the new account a member of the inviting household instead of
 * giving it an empty one of its own; the server falls back to that if the invite is no
 * longer valid.
 */
export async function signUp(
  name: string,
  email: string,
  password: string,
  inviteToken: string | null = null,
): Promise<Session> {
  await authJson('/api/auth/sign-up/email', {
    name,
    email,
    password,
    ...(inviteToken == null ? {} : { inviteToken }),
  });
  const session = await fetchSession();
  if (session == null) throw new Error('Account created, but no session was returned');
  return session;
}

export function requestPasswordReset(email: string): Promise<{ status: boolean }> {
  return authJson('/api/auth/request-password-reset', {
    email,
    redirectTo: `${window.location.origin}/reset-password`,
  });
}

export function resetPassword(token: string, newPassword: string): Promise<{ status: boolean }> {
  return authJson('/api/auth/reset-password', { token, newPassword });
}

/**
 * Changes the password of the signed-in account.
 *
 * Goes straight to Better Auth rather than through a route of ours on purpose. With
 * `revokeOtherSessions` it deletes every session — this one included — and mints a
 * replacement, handed back as a `Set-Cookie`. Calling its handler directly means the
 * browser picks that up by itself; a proxy would have to forward the header by hand, and
 * forgetting to presents as "changing my password signs me out at random".
 */
export function changePassword(
  currentPassword: string,
  newPassword: string,
  revokeOtherSessions: boolean,
): Promise<{ status: boolean }> {
  return authJson('/api/auth/change-password', {
    currentPassword,
    newPassword,
    revokeOtherSessions,
  });
}

export async function signOut(): Promise<void> {
  window.localStorage.removeItem(SESSION_KEY);
  await json('/api/auth/sign-out', { method: 'POST', body: JSON.stringify({}) });
}

// ---------------------------------------------------------------- account

/**
 * Everything the Security screen needs, in one request.
 *
 * First-party routes rather than Better Auth's own `list-sessions`, which returns every
 * session's raw token — the web app uses cookies precisely so credentials stay out of
 * reach of page scripts. The server answers `current` instead, which is the only place
 * it can be answered: the browser cannot read its own httpOnly cookie.
 */
export function accountSecurity(): Promise<AccountSecurityResponse> {
  return json('/api/account/security');
}

export function revokeSession(sessionId: string): Promise<{ ok: true }> {
  return json(`/api/account/sessions/${encodeURIComponent(sessionId)}/revoke`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function revokeOtherSessions(): Promise<{ ok: true; revoked: number }> {
  return json('/api/account/sessions/revoke-others', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function accountDeletionPreview(): Promise<AccountDeletionPreview> {
  return json('/api/account/deletion-preview');
}

/**
 * Deletes the account for good. The server has already dropped every session by the time
 * this resolves, so the cached session goes too: a reload must land on sign-in, not on a
 * cold-start copy of somebody who no longer exists.
 */
export async function deleteAccount(input: DeleteAccountRequest): Promise<void> {
  await json('/api/account/delete', { method: 'POST', body: JSON.stringify(input) });
  window.localStorage.removeItem(SESSION_KEY);
}

/**
 * Forgets everything this browser remembers about who is signed in and what they had open.
 *
 * The two household keys are device-local and belong to nobody in particular — the sync
 * client keeps one active household and one cached list per browser, not per account — so
 * crossing between an agent and a customer has to drop them. Without it the app opens
 * against a household the person it is now acting as is not a member of, and the cached
 * list briefly shows them somebody else's households.
 */
function forgetLocalIdentity(): void {
  window.localStorage.removeItem(SESSION_KEY);
  window.localStorage.removeItem(ACTIVE_HOUSEHOLD_KEY);
  window.localStorage.removeItem(HOUSEHOLD_LIST_KEY);
}

/**
 * Starts acting as another user. Support-console only; the server re-checks the permission.
 *
 * The caller is expected to follow this with a full page load rather than a route change:
 * the household manager, its sockets and every parsed document belong to the previous
 * identity, and tearing them down by hand is far more fragile than starting clean.
 */
export async function startImpersonation(userId: string): Promise<{ name: string }> {
  const result = await json<{ ok: true; name: string }>('/api/admin/impersonate', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });
  forgetLocalIdentity();
  return result;
}

/** Ends impersonation and hands the browser back to the admin's own account. */
export async function stopImpersonation(): Promise<void> {
  await json('/api/impersonation/stop', { method: 'POST', body: JSON.stringify({}) });
  forgetLocalIdentity();
}

export async function listHouseholds(): Promise<HouseholdSummary[]> {
  const { households } = await json<{ households: HouseholdSummary[] }>('/api/households');
  return households;
}

export function renameHousehold(
  householdId: string,
  name: string,
): Promise<{ id: string; name: string }> {
  return json(`/api/households/${householdId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

export function listMembers(householdId: string): Promise<{
  members: { userId: string; name: string; email: string; role: string }[];
}> {
  return json(`/api/households/${householdId}/members`);
}

export function listPendingInvites(householdId: string): Promise<{
  invites: { id: string; email: string; role: string; createdAt: number; expiresAt: number }[];
}> {
  return json(`/api/households/${householdId}/invites`);
}

export function inviteMember(
  householdId: string,
  email: string,
  role: 'owner' | 'editor' | 'viewer',
): Promise<{ id: string; url: string; expiresAt: number }> {
  return json(`/api/households/${householdId}/invites`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
}

// ---------------------------------------------------------------- billing

export function getBilling(householdId: string): Promise<BillingSummary> {
  return json(`/api/billing/${householdId}`);
}

/**
 * Mints the nonce to attach as a RevenueCat subscriber attribute before checkout opens.
 * Throws `already_subscribed` (409) when another owner got there first.
 */
export function createCheckout(householdId: string): Promise<CheckoutResponse> {
  return json('/api/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ householdId }),
  });
}

export function billingPortal(householdId: string): Promise<{ url: string }> {
  return json(`/api/billing/${householdId}/portal`, { method: 'POST', body: JSON.stringify({}) });
}

/**
 * Where to fix a failed renewal: a Stripe portal session for a web subscription, so the
 * same subscription pays its open invoice. Refused with `manage_in_store` for app stores.
 */
export function reactivateBilling(householdId: string): Promise<{ url: string }> {
  return json(`/api/billing/${householdId}/reactivate`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function revokeInvite(householdId: string, inviteId: string): Promise<{ ok: true }> {
  return json(`/api/households/${householdId}/invites/${encodeURIComponent(inviteId)}`, {
    method: 'DELETE',
  });
}

export interface InvitePreview {
  email: string;
  householdName: string;
  invitedByName: string;
}

/** Public: works signed out, so the invite link can greet someone who has no account yet. */
export function fetchInvitePreview(token: string): Promise<InvitePreview> {
  return json(`/api/invites/${encodeURIComponent(token)}`);
}

export function acceptInvite(token: string): Promise<{ householdId: string }> {
  return json(`/api/invites/${token}/accept`, { method: 'POST', body: JSON.stringify({}) });
}

export function createShare(input: {
  householdId: string;
  kind: 'recipe' | 'collection';
  recipeIds: string[];
  title: string;
}): Promise<{ shareId: string; url: string; token: string }> {
  return json('/api/shares', { method: 'POST', body: JSON.stringify(input) });
}

/**
 * Grants the household the share's photo blobs and hands back the manifest. Nothing is
 * written to the recipe book here — the client applies the manifest to its own document,
 * which is what makes the copy independent of the sender.
 */
export function importShare(
  token: string,
  input: { householdId: string },
): Promise<{ manifest: ShareManifest; alreadyImported: boolean }> {
  return json(`/api/shares/${token}/import`, { method: 'POST', body: JSON.stringify(input) });
}

export function createImportSession(householdId: string, files: File[]): Promise<ImportSessionDto> {
  const form = new FormData();
  for (const file of files) form.append('files', file);
  return fetch(`${API_BASE_URL}/api/households/${householdId}/imports`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  }).then(async (response) => {
    if (!response.ok) throw await apiError(response);
    return (await response.json()) as ImportSessionDto;
  });
}

/** Starts one import session covering every URL in the batch. */
export function createUrlImportSession(
  householdId: string,
  urls: string[],
): Promise<ImportSessionDto> {
  return json(`/api/households/${householdId}/imports`, {
    method: 'POST',
    body: JSON.stringify({ urls }),
  });
}

export function listImportSessions(householdId: string): Promise<ImportSessionSummaryDto[]> {
  return json<{ sessions: ImportSessionSummaryDto[] }>(
    `/api/households/${householdId}/imports`,
  ).then((body) => body.sessions);
}

export function getImportSession(householdId: string, importId: string): Promise<ImportSessionDto> {
  return json(`/api/households/${householdId}/imports/${importId}`);
}

export function patchImportFile(
  householdId: string,
  importId: string,
  fileId: string,
  patch: { status: ImportFileDto['status']; recipeId?: string; error?: string },
): Promise<ImportFileDto> {
  return json(`/api/households/${householdId}/imports/${importId}/files/${fileId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * Asks the server to run this file's AI extraction. Large sessions arrive with nothing
 * queued so the client can spread these across its token budget; safe to call again for
 * a file that already failed, and a no-op for one already past `pending`.
 */
export function requestImportFileExtraction(
  householdId: string,
  importId: string,
  fileId: string,
): Promise<ImportFileDto> {
  return json(`/api/households/${householdId}/imports/${importId}/files/${fileId}/extract`, {
    method: 'POST',
  });
}

export async function fetchImportFileContent(
  householdId: string,
  importId: string,
  fileId: string,
): Promise<string> {
  const response = await fetch(
    `${API_BASE_URL}/api/households/${householdId}/imports/${importId}/files/${fileId}/content`,
    { credentials: 'include' },
  );
  if (!response.ok) throw new Error(`request failed (${response.status})`);
  return response.text();
}

export async function fetchImportFileResult(
  householdId: string,
  importId: string,
  fileId: string,
): Promise<string> {
  const response = await fetch(
    `${API_BASE_URL}/api/households/${householdId}/imports/${importId}/files/${fileId}/result`,
    { credentials: 'include' },
  );
  if (!response.ok) throw new Error(`request failed (${response.status})`);
  return response.text();
}

export function importRecipeImage(householdId: string, url: string): Promise<ImportImageResponse> {
  return json(`/api/households/${householdId}/import-images`, {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
}

// ---------------------------------------------------------------- admin

export interface AdminListParams {
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
  q: string;
}

/** Defaults are left off the wire so a shared link stays readable. */
function adminQuery(params: AdminListParams): string {
  const search = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
    sort: params.sort,
    dir: params.dir,
  });
  if (params.q !== '') search.set('q', params.q);
  return search.toString();
}

export function adminOverview(): Promise<AdminOverview> {
  return json('/api/admin/overview');
}

export function listAdminUsers(params: AdminListParams): Promise<AdminListResponse<AdminUserRow>> {
  return json(`/api/admin/users?${adminQuery(params)}`);
}

export function listAdminHouseholds(
  params: AdminListParams,
): Promise<AdminListResponse<AdminHouseholdRow>> {
  return json(`/api/admin/households?${adminQuery(params)}`);
}

/** Creates a household, comped or free, with every address invited to it; answers its id. */
export function createInvitedHousehold(
  request: CreateInvitedHouseholdRequest,
): Promise<CreateInvitedHouseholdResponse> {
  return json('/api/admin/households', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export function getAdminHousehold(householdId: string): Promise<AdminHouseholdDetail> {
  return json(`/api/admin/households/${encodeURIComponent(householdId)}`);
}

/**
 * Comps a household, and answers with the household as it now stands.
 *
 * The reply is the whole detail payload rather than an acknowledgement, so the page
 * re-renders from what the server actually wrote instead of predicting it.
 */
export function grantComplimentary(
  householdId: string,
  reason: string,
): Promise<AdminHouseholdDetail> {
  return json(`/api/admin/households/${encodeURIComponent(householdId)}/complimentary`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}
